import { Stack, Avatar, Box, Chip, Skeleton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { Player } from '@oskarjiang/matchi-types';

type Props = {
	participant: Player;
	averageLevel?: number;
	isLoading: boolean;
};
const hasNoName = (participant: Player) => !participant.firstName && !participant.lastName;

export const ListItem = ({ participant, averageLevel, isLoading }: Props) => {
	const theme = useTheme();

	// Generate a consistent color based on the user name or ID
	const generateAvatarColor = () => {
		const name = hasNoName(participant) ? `user-${participant.userId}` : `${participant.firstName} ${participant.lastName}`;
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}
		const hue = hash % 360;
		return `hsl(${hue}, 70%, 80%)`;
	};
	return (
		<Paper
			elevation={0}
			sx={{
				p: { xs: 1, sm: 0.5 },
				m: 0.5,
				borderRadius: 2,
				backgroundColor: !isLoading && !averageLevel ? 'rgba(244, 199, 199, 0.4)' : theme.palette.background.default,
				transition: 'transform 0.2s, box-shadow 0.2s',
				'&:hover': {
					transform: 'translateY(-2px)',
					boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
				},
				width: 'calc(100% - 8px)',
			}}
		>
			<Stack direction="row" spacing={2} alignItems="center">
				{/* Avatar */}
				<Avatar
					src={participant.profileImageURL ?? undefined}
					sx={{
						width: { xs: 48, sm: 56 },
						height: { xs: 48, sm: 56 },
						bgcolor: generateAvatarColor(),
						border: `2px solid ${theme.palette.background.paper}`,
						boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
						fontSize: { xs: '1rem', sm: '1.2rem' },
						fontWeight: 'bold',
						flexShrink: 0,
					}}
				>
					{hasNoName(participant) ? 'A' : `${participant.firstName?.charAt(0)}${participant.lastName?.charAt(0)}`}
				</Avatar>

				{/* Player Info */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="subtitle1"
						fontWeight={500}
						sx={{
							wordBreak: 'break-word',
							overflowWrap: 'break-word',
							fontSize: { xs: '0.9rem', sm: '1rem' },
						}}
					>
						{hasNoName(participant) ? `Anonymous Player` : `${participant.firstName} ${participant.lastName}`}
					</Typography>
					{isLoading ? (
						<Skeleton width="40%" height={24} />
					) : averageLevel ? (
						<Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
							{averageLevel.toPrecision(2)}
						</Typography>
					) : (
						<Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
							-
						</Typography>
					)}
				</Box>

				{/* Registered level */}
				{participant.level && (
					<Tooltip title="Registered level">
						<Chip
							label={participant.level}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ flexShrink: 0, fontWeight: 600 }}
						/>
					</Tooltip>
				)}
			</Stack>
		</Paper>
	);
};
